import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { action, computed, observable, makeObservable, runInAction } from 'mobx';
import { getRoot } from 'mobx-easy';
import { cloneDeep as _cloneDeep } from 'lodash';
import { ALERT_TYPES, Alert as BringgAlert, Task } from '@bringg/types';
import { AlertDisplayCategory } from '@bringg/types/types/alert';
import { throttleWithBuffer } from '@bringg-frontend/utils';
import type { BufferedItem, ThrottledFuncWithBuffer } from '@bringg-frontend/utils';

import notification from 'bringg-web/services/notification';
import BaseDomainStore from '../core/base-domain-store';
import Alert from './domain-object/alert';
import {
	APP_CONFIG,
	DEFAULT_OPEN_TASK_FIELDS,
	DEFAULT_ALERT_REALTIME_EVENTS_BATCHING_TIME,
	TASK_ALERTS,
	DRIVER_ALERTS,
	OPTIMIZATION_ALERTS
} from '../../consts';
import { alertRunsProvider } from 'bringg-web/stores/alerts/alert-runs-provider';
import type RootStore from '../root-store';
import type { AlertGroupWithAll, ElasticAlert, GetAlertsParams } from '../../types/common.consts';
import { workflowsRootStore } from 'bringg-web/features/automation-workflows/stores/workflows-root-store';

export const INITIAL_TASK_ALERTS_SIZE = 50;

export enum AlertRealTimeAction {
	Create,
	Delete,
	UpdateOwnership
}

class AlertsStore extends BaseDomainStore<Alert> {
	private shouldLoadTaskOnAlertSocketEvent = false;
	private newAlertSubscription: () => void;
	private removeAlertSubscription: () => void;
	private updateOwnershipSubscription: () => void;
	private socketEventsBatcher: ThrottledFuncWithBuffer<ElasticAlert, AlertRealTimeAction>;

	isAllAlertsLoaded = false;
	isAllTaskAlertsDataLoaded = false;
	isSomeTaskAlertsReady = false;
	isInitialTaskAlertsDataLoading = false;
	isMoreTaskAlertsDataLoading = false;
	taskAlerts: Map<string, Alert> = new Map();
	driverAlerts: Map<string, Alert> = new Map();
	optimizationAlerts: Map<string, Alert> = new Map();
	tasks: Map<number, Task> = new Map();

	constructor() {
		super();

		makeObservable(this, {
			tasks: observable,
			getTasks: computed,
			updateTask: action,
			deleteTask: action,
			isAllAlertsLoaded: observable,
			setIsAllAlertsLoaded: action,
			isMoreTaskAlertsDataLoading: observable,
			setIsMoreTaskAlertsDataLoading: action,
			isAllTaskAlertsDataLoaded: observable,
			setIsAllTaskAlertsDataLoaded: action,
			isSomeTaskAlertsReady: observable,
			setIsSomeTaskAlertsReady: action,

			taskAlerts: observable.shallow,
			allTaskAlerts: computed,
			exceptions: computed,
			driverAlerts: observable.shallow,
			allDriverAlerts: computed,
			optimizationAlerts: observable.shallow,
			allOptimizationAlerts: computed,

			setBatchTaskAlerts: action,
			setAlerts: action,
			removeAlert: action,
			removeBatchAlerts: action,
			ownedAlertsInfo: computed,
			availableRouteIds: computed,
			visibleAlertsTotalCount: computed,

			alertTypesFilteredByConfig: computed,
			alertSetsFilteredByConfig: computed
		});
	}

	get getTasks() {
		return this.tasks;
	}

	updateTask(task) {
		const clonedTask = _cloneDeep(task);
		clonedTask.task_inventories = undefined;

		this.tasks.set(task.id, clonedTask);
	}

	deleteTask(taskId) {
		this.tasks.delete(taskId);
	}

	setShouldLoadTaskOnAlertSocketEvent = (value: boolean) => {
		this.shouldLoadTaskOnAlertSocketEvent = value;
	};

	onEventsBatch = async (eventsBatch: BufferedItem<ElasticAlert, AlertRealTimeAction>[]) => {
		const { tasksStore } = getRoot<RootStore>().data;
		const { toCreate, toUpdate, toDelete } = eventsBatch.reduce<Record<string, ElasticAlert[]>>(
			(acc, { _action, ...eventData }) => {
				if (_action === AlertRealTimeAction.Create) {
					acc.toCreate.push(eventData);
				} else if (_action === AlertRealTimeAction.UpdateOwnership) {
					acc.toUpdate.push(eventData);
				} else if (_action === AlertRealTimeAction.Delete) {
					acc.toDelete.push(eventData);
				}
				return acc;
			},
			{ toCreate: [], toUpdate: [], toDelete: [] }
		);

		// To prevent triggering "heavy" loadMany for all users
		// current idea trigger it only if Alerts page were visited
		// more sophisticated solution might be implemented if needed
		if (this.shouldLoadTaskOnAlertSocketEvent) {
			const taskIdsToEnsure = toCreate.reduce<number[]>((ids, { task_id }) => {
				if (task_id) {
					ids.push(task_id);
				}
				return ids;
			}, []);

			this.setIsMoreTaskAlertsDataLoading(true);
			const loadedTasks = await tasksStore.loadMany(taskIdsToEnsure);

			runInAction(() => {
				loadedTasks.forEach(task => this.updateTask(task));
			});

			this.setIsMoreTaskAlertsDataLoading(false);
		}

		runInAction(() => {
			this.onNewAlertsEvent(toCreate);
			this.onUpdateOwnership(toUpdate);
			this.removeBatchAlerts(toDelete.map(({ uuid }) => uuid));
		});
	};

	afterUserLoggedIn() {
		const { alertsApi } = getRootEnv().dashboardSdk.sdk;
		const { tasksStore } = getRoot<RootStore>().data;

		alertsApi.attachSubscriptions();
		if (APP_CONFIG.ALERTS_REAL_TIME_UPDATE) {
			// TODO: remove else branch and use batching by default if no complains after middle of Q3 2023
			//  https://bringg.atlassian.net/browse/BRNGG-22021
			if (APP_CONFIG.ALERTS_REAL_TIME_BATCHING) {
				this.socketEventsBatcher = throttleWithBuffer<ElasticAlert, AlertRealTimeAction>(
					this.onEventsBatch,
					// make it configurable via merchant_config
					DEFAULT_ALERT_REALTIME_EVENTS_BATCHING_TIME,
					{
						dataBufferingHandler: (items, prevBuffer, action) => {
							items.forEach(item => {
								Object.assign(item, { _action: action });
							});
							prevBuffer.push(...items);
							return prevBuffer;
						}
					}
				);

				this.newAlertSubscription = alertsApi.subscribeOnCreate(alerts =>
					this.socketEventsBatcher(alerts, AlertRealTimeAction.Create)
				);
				this.removeAlertSubscription = alertsApi.subscribeOnDelete(alert =>
					this.socketEventsBatcher([alert], AlertRealTimeAction.Delete)
				);
				this.updateOwnershipSubscription = alertsApi.subscribeOnUpdateOwnership(alerts =>
					this.socketEventsBatcher(alerts, AlertRealTimeAction.UpdateOwnership)
				);
			} else {
				this.newAlertSubscription = alertsApi.subscribeOnCreate(this.onNewAlertsEvent);
				this.removeAlertSubscription = alertsApi.subscribeOnDelete(this.onRemoveAlertEvent);
				this.updateOwnershipSubscription = alertsApi.subscribeOnUpdateOwnership(this.onUpdateOwnership);
			}
		}

		tasksStore.onUpdate(data => {
			if (this.tasks.has(data.current.id)) {
				this.updateTask(data.current);
			}
		});

		tasksStore.onCreate(data => {
			if (this.tasks.has(data.current.id)) {
				this.updateTask(data.current);
			}
		});

		tasksStore.onDelete(data => {
			this.deleteTask(data.diff.id);
		});
	}

	dispose = () => {
		if (this.socketEventsBatcher) {
			this.socketEventsBatcher.cancel();
			this.socketEventsBatcher = null;
		}

		if (this.newAlertSubscription) this.newAlertSubscription();
		if (this.removeAlertSubscription) this.removeAlertSubscription();
		if (this.updateOwnershipSubscription) this.updateOwnershipSubscription();
	};

	afterUserLoggedOut() {
		this.dispose();
	}

	getTaskAlert = (id: string) => this.taskAlerts.get(id);

	setBatchTaskAlerts = (items: Alert[]) => {
		items.forEach(alert => {
			this.taskAlerts.set(alert.uuid, alert);
			this.set(alert);
		});
	};

	removeAlert = (uuid: string) => {
		const {
			alertsViewStore: { unseenAlertsManager }
		} = getRoot<RootStore>().data;

		// TODO remove it from one alert source
		this.taskAlerts.delete(uuid);
		this.driverAlerts.delete(uuid);
		this.optimizationAlerts.delete(uuid);
		this.remove(uuid);

		unseenAlertsManager.remove(uuid);
	};

	removeBatchAlerts = (uuids: string[]) => {
		uuids.forEach(uuid => {
			this.removeAlert(uuid);
		});
	};

	setIsAllAlertsLoaded = (loaded = true) => {
		this.isAllAlertsLoaded = loaded;
	};

	setIsSomeTaskAlertsReady = (ready = true) => {
		this.isSomeTaskAlertsReady = ready;
	};

	setIsAllTaskAlertsDataLoaded = (loaded = true) => {
		this.isAllTaskAlertsDataLoaded = loaded;
	};

	setIsMoreTaskAlertsDataLoading = (loading: boolean) => {
		this.isMoreTaskAlertsDataLoading = loading;
	};

	private fetchDataForInitialAlertsSet = async (alerts: Pick<BringgAlert, 'task_id'>[]): Promise<void> => {
		if (!alerts?.length) {
			return;
		}
		const { tasksStore } = getRoot<RootStore>().data;
		const taskIdsToFetch = Array.from(
			new Set(
				alerts.reduce<number[]>((ids, { task_id }) => {
					if (task_id) {
						ids.push(task_id);
					}
					return ids;
				}, [])
			)
		);

		if (taskIdsToFetch.length) {
			// Must be sure that tasks in the store before asking runs for them
			const loadedTasks = await tasksStore.loadMany(taskIdsToFetch);
			loadedTasks.forEach(task => this.updateTask(task));
			await alertRunsProvider.load(taskIdsToFetch);
		}
	};

	private fetchDataForRestAlertsSet = async (
		alerts: Pick<BringgAlert, 'task_id' | 'alert_type'>[]
	): Promise<void> => {
		if (!alerts?.length) {
			return;
		}
		const { tasksStore } = getRoot<RootStore>().data;

		const [openTasks, planningTasks] = await Promise.all([
			// tasks/open add limit:3000 under the hood on the back-end if limit not specified, not sure about tasks/planning
			// make sure alerts related tasks are fetched, for now - all open tasks
			// TODO: move to using only batchFetch if and when it has a POST implementation
			tasksStore.loadOpen(),
			tasksStore.loadPlanning(DEFAULT_OPEN_TASK_FIELDS)
		]);

		runInAction(() => {
			openTasks.forEach(task => this.updateTask(task));
			planningTasks.forEach(task => this.updateTask(task));
		});

		// All alerts displayed alerts should be related to open tasks, except (for now the only type) DRIVER_CANCELLED_ORDER
		// need to make sure related to those alerts tasks are present in store
		const taskIdsToEnsure = alerts.reduce<number[]>((idsToFetch, { task_id, alert_type }) => {
			if (task_id && alert_type === ALERT_TYPES.DRIVER_CANCELLED_ORDER) {
				idsToFetch.push(task_id);
			}
			return idsToFetch;
		}, []);
		if (taskIdsToEnsure.length) {
			const loadedTasks = await tasksStore.loadMany(taskIdsToEnsure);

			runInAction(() => {
				loadedTasks.forEach(task => this.updateTask(task));
			});
		}

		await alertRunsProvider.load(
			Array.from(
				new Set(
					alerts.reduce<number[]>((ids, { task_id }) => {
						if (task_id) {
							ids.push(task_id);
						}
						return ids;
					}, [])
				)
			)
		);
	};

	fetchTaskAlertsData = async () => {
		const {
			isInitialTaskAlertsDataLoading,
			isMoreTaskAlertsDataLoading,
			isAllTaskAlertsDataLoaded,
			isAllAlertsLoaded
		} = this;

		// protect from loading data without loaded alerts
		if (!isAllAlertsLoaded) {
			return;
		}

		// protect from re-fetch and concurrent fetch
		if (isAllTaskAlertsDataLoaded || isInitialTaskAlertsDataLoading || isMoreTaskAlertsDataLoading) {
			return;
		}

		const { allTaskAlerts: taskAlerts } = this;

		try {
			if (APP_CONFIG.ALERTS_TABLE_FAST_INITIALIZATION) {
				// restAlerts will be [] if taskAlerts.length <= INITIAL_TASK_ALERTS_SIZE
				const [initialAlerts, restAlerts] = [
					taskAlerts.slice(0, INITIAL_TASK_ALERTS_SIZE),
					taskAlerts.slice(INITIAL_TASK_ALERTS_SIZE)
				];

				this.isInitialTaskAlertsDataLoading = true;
				await this.fetchDataForInitialAlertsSet(initialAlerts);
				this.setIsSomeTaskAlertsReady(true);

				this.setIsMoreTaskAlertsDataLoading(true);
				await this.fetchDataForRestAlertsSet(restAlerts);
			} else {
				this.isInitialTaskAlertsDataLoading = true;
				await this.fetchDataForRestAlertsSet(taskAlerts);
				this.setIsSomeTaskAlertsReady(true);
			}
			this.setIsAllTaskAlertsDataLoaded(true);
		} catch (e) {
			notification.error('Failed to fetch Alerts related Tasks data');
			console.error(e);
		} finally {
			this.isInitialTaskAlertsDataLoading = false;
			this.setIsMoreTaskAlertsDataLoading(false);
		}
	};

	// For proper Task Alerts display fetchTaskAlertsData should also be called
	fetchAlerts = async (params?: GetAlertsParams) => {
		if (this.isAllAlertsLoaded) {
			return;
		}

		const { alertsApi } = getRootEnv().dashboardSdk.sdk;
		const { merchantConfigurationsStore } = getRoot<RootStore>().data;
		await merchantConfigurationsStore.fetch();
		const { workflowAlertsStore } = workflowsRootStore.getStore();
		await workflowAlertsStore.load();

		const configurations = workflowAlertsStore.configurationsFilteredByUserRole;
		const mergedAlerts = [...this.alertTypesFilteredByConfig.all, ...configurations];

		try {
			const alerts: BringgAlert[] = mergedAlerts.length
				? await alertsApi.getAlertsKeySetPagination({
						alertTypes: mergedAlerts,
						...params
				  })
				: [];

			if (alerts.length) {
				this.setAlerts(alerts);
			}

			this.setIsAllAlertsLoaded(true);
		} catch (e) {
			notification.error('Failed to fetch Alerts');
			console.error('failed to fetch alerts', e);
		}
	};

	get allDriverAlerts() {
		return Array.from(this.driverAlerts.values()).map(item => item.toJSObject);
	}

	get allOptimizationAlerts() {
		return Array.from(this.optimizationAlerts.values()).map(item => item.toJSObject);
	}

	get allTaskAlerts() {
		return Array.from(this.taskAlerts.values());
	}

	get exceptions() {
		// Triggered if task added since alert.exception uses tasks
		// Filter out null since alert.exception possibly return it
		return this.allTaskAlerts.map(alert => alert.exception).filter(Boolean);
	}

	get ownedAlertsInfo(): { numberOfAlerts: number; numberOfOrders: number } {
		const {
			usersStore: { currentUser }
		} = getRoot<RootStore>().data;

		const alerts: Alert[] = [];
		const orderIds: Set<number> = new Set();

		this.all.forEach(alert => {
			if (alert.owner_id === currentUser.id) {
				alerts.push(alert);
				orderIds.add(alert.task_id);
			}
		});

		return {
			numberOfAlerts: alerts.length,
			numberOfOrders: Array.from(orderIds.values()).length
		};
	}

	get availableRouteIds() {
		return new Set(this.exceptions.map(({ routeId }) => routeId));
	}

	// Safe, in terms of task relation and enabled alert types, method
	setAlerts = (alerts: ElasticAlert[], addToUnseen = false) => {
		const {
			alertsViewStore: { unseenAlertsManager }
		} = getRoot<RootStore>().data;
		const { taskAlerts, driverAlerts, optimizationAlerts } = this.alertSetsFilteredByConfig;
		const { workflowAlertsStore } = workflowsRootStore.getStore();

		const { storeTaskAlerts, storeDriverAlerts, storeOptimizationAlerts } = alerts.reduce<Record<string, Alert[]>>(
			(acc, alert) => {
				const { task_id, alert_type, extra_data } = alert;
				const displayCategory = extra_data?.['display_category'];

				const isConfigurationMatchUserRole =
					workflowAlertsStore.configurationsFilteredByUserRole.has(alert_type);
				if (alert.extra_data?.custom_alert && !isConfigurationMatchUserRole) {
					return acc;
				}

				if (task_id && (taskAlerts.has(alert_type) || displayCategory === AlertDisplayCategory.TASK)) {
					acc.storeTaskAlerts.push(new Alert(alert));
				} else if (driverAlerts.has(alert_type) || displayCategory === AlertDisplayCategory.DRIVER) {
					acc.storeDriverAlerts.push(new Alert(alert));
				} else if (
					optimizationAlerts.has(alert_type) ||
					displayCategory === AlertDisplayCategory.OPTIMIZATION
				) {
					acc.storeOptimizationAlerts.push(new Alert(alert));
				}
				return acc;
			},
			{ storeTaskAlerts: [], storeDriverAlerts: [], storeOptimizationAlerts: [] }
		);

		this.setBatchTaskAlerts(storeTaskAlerts);

		storeDriverAlerts.forEach(alert => {
			this.driverAlerts.set(alert.uuid, alert);
			this.set(alert);
		});

		storeOptimizationAlerts.forEach(alert => {
			this.optimizationAlerts.set(alert.uuid, alert);
			this.set(alert);
		});

		if (addToUnseen) {
			unseenAlertsManager.add(
				'taskAlerts',
				storeTaskAlerts.map(alert => alert.id)
			);
			unseenAlertsManager.add(
				'driverAlerts',
				storeDriverAlerts.map(alert => alert.id)
			);
			unseenAlertsManager.add(
				'optimizationAlerts',
				storeOptimizationAlerts.map(alert => alert.id)
			);
		}
	};

	onRemoveAlertEvent = alertToRemove => {
		const alert = this.items.get(alertToRemove.uuid);

		if (alert) {
			this.removeAlert(alert.uuid);
		}
	};

	// websocket event handlers
	onNewAlertsEvent = (alerts: any[]) => {
		// TODO: remove this forEach after socket event will be extend with: created_at and id;
		const newAlerts = alerts.map(alert => ({
			...alert,
			// new alert event don't contains date of alert, but if we recieve the event => means that alert created just now
			created_at: alert.created_at ?? new Date().toISOString(),
			// new alert event don't contains id of alert(only uuid)
			id: `merchant_id:${alert.merchant_id}:alert_type:${alert.alert_type}:task_id:${alert.task_id}`
		}));
		// TODO: check if we can be sure that alert related task is present in store at this point
		//  not only task, but all related entities related to alert through task i.e. Run
		this.setAlerts(newAlerts, true);
	};

	onUpdateOwnership = alerts => {
		runInAction(() => {
			alerts.forEach(item => {
				const alert = this.get(item.uuid);
				if (alert) alert.set({ owner_id: item.owner_id });
			});
		});
	};

	dismissAlertsByIds = async (ids: string[]) => {
		const response = await getRootEnv().dashboardSdk.sdk.alertsApi.batchDismiss(ids);

		if (response.success) {
			this.removeBatchAlerts(ids);
		}

		return response;
	};

	updateOwnership = async (alertUuids: string[], userId: number = null) => {
		// TODO: fix sdk return type
		const response: any = await getRootEnv().dashboardSdk.sdk.alertsApi.updateOwnership({
			alertUuids,
			userId
		});

		if (response.success) {
			const updater = { owner_id: userId };
			alertUuids.forEach(uuid => {
				const alert = this.items.get(uuid);
				if (alert) alert.set(updater);
			});
		}
	};

	getRelatedAlerts = (uuid: string, ownerId: number) => {
		const currentAlert = this.get(uuid);
		if (!currentAlert) {
			return [];
		}

		const relatedOrderAlerts = this.all.filter(alert => alert.task_id && alert.task_id === currentAlert.task_id);

		return ownerId
			? relatedOrderAlerts.filter(alert => alert.owner_id === ownerId)
			: relatedOrderAlerts.filter(alert => !alert.owner_id);
	};

	get visibleAlertsTotalCount(): number {
		const { driverAlerts, optimizationAlerts, taskAlerts } = this;
		if (this.isAllTaskAlertsDataLoaded) {
			return this.exceptions.length + driverAlerts.size + optimizationAlerts.size;
		}
		return taskAlerts.size + driverAlerts.size + optimizationAlerts.size;
	}

	// TODO: make sure this computed is memoized when called from another store (not being wrapped in observer)
	get alertTypesFilteredByConfig(): Record<AlertGroupWithAll, ALERT_TYPES[]> {
		const { isAdmin, isDispatcher } = getRoot<RootStore>().data.usersStore;
		const { alert_configurations } = getRoot<RootStore>().data.merchantConfigurationsStore.configuration;
		const types = {
			taskAlerts: TASK_ALERTS,
			driverAlerts: DRIVER_ALERTS,
			optimizationAlerts: OPTIMIZATION_ALERTS,
			all: TASK_ALERTS.concat(DRIVER_ALERTS, OPTIMIZATION_ALERTS)
		};

		if (alert_configurations) {
			Object.keys(types).forEach(key => {
				types[key] = types[key].filter(type => {
					if (
						// If config for alert does not exist we treat it as enabled to match the backend behavior
						!alert_configurations[type] &&
						// Day & Ross have custom predefined alert, and it should not be displayed to other merchants if not configured
						type < ALERT_TYPES.TMP_DAY_AND_ROSS_ORDER_TOTAL_WEIGHT_CHANGED
					) {
						return true;
					}

					return (
						(alert_configurations[type]?.dispatcher && isDispatcher) ||
						(alert_configurations[type]?.admin && isAdmin)
					);
				});
			});
		}

		return types;
	}

	// TODO: make sure this computed is memoized when called from another store (not being wrapped in observer)
	get alertSetsFilteredByConfig(): Record<AlertGroupWithAll, Set<ALERT_TYPES>> {
		const { taskAlerts, driverAlerts, optimizationAlerts, all } = this.alertTypesFilteredByConfig;
		return {
			taskAlerts: new Set(taskAlerts),
			driverAlerts: new Set(driverAlerts),
			optimizationAlerts: new Set(optimizationAlerts),
			all: new Set(all)
		};
	}
}

export default AlertsStore;
